import { toLeagueDisplay } from './league-display-names'

declare global {
  interface Window {
    dataLayer?: Record<string, unknown>[]
    gtag?: (...args: unknown[]) => void
  }
}

const MEASUREMENT_ID = 'G-V4SB19928R'
const ALL_PARAMS = ['odds_league', 'odds_league_display', 'odds_sportsbook', 'odds_selection_name', 'odds_market_name', 'odds_event_name', 'odds_host_domain', 'map_state', 'map_book_kind', 'map_theme', 'map_scope', 'map_tab', 'map_nav', 'map_status', 'map_search_term', 'map_search_hits'] as const

let initialized = false

export function initAnalytics() {
  if (initialized || typeof window === 'undefined')
    return
  initialized = true

  // Set consent defaults — cookieless until host page grants consent
  window.dataLayer = window.dataLayer || []
  window.gtag = function gtag() {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer!.push(arguments as unknown as Record<string, unknown>)
  }
  window.gtag('consent', 'default', {
    analytics_storage: 'granted',
  })
  window.gtag('consent', 'default', {
    analytics_storage: 'denied',
    region: ['EU', 'GB', 'IS', 'LI', 'NO', 'CH'],
  })
  window.gtag('js', new Date())
  window.gtag('config', MEASUREMENT_ID, { send_page_view: false })

  // Load gtag.js script
  const script = document.createElement('script')
  script.async = true
  script.src = `https://www.googletagmanager.com/gtag/js?id=${MEASUREMENT_ID}`
  document.head.appendChild(script)
}

function track(eventName: string, params: Record<string, string>) {
  if (typeof window === 'undefined' || !window.gtag)
    return
  const clean: Record<string, string | undefined> = {}
  for (const key of ALL_PARAMS)
    clean[key] = key in params ? params[key] : undefined
  clean.odds_host_domain = window.location.hostname
  clean.odds_league_display = params.odds_league ? toLeagueDisplay(params.odds_league) : undefined
  window.gtag('event', eventName, clean)
}

export function trackWidgetView(league: string) {
  track('odds_widget_view', { odds_league: league })
}

export function trackOddsClick(league: string, sportsbook: string, selectionName: string, marketName: string, eventName: string) {
  track('odds_widget_odds_click', { odds_league: league, odds_sportsbook: sportsbook, odds_selection_name: selectionName, odds_market_name: marketName, odds_event_name: eventName })
}

export function trackPlaceBet(league: string, sportsbook: string, selectionName: string, marketName: string, eventName: string) {
  track('odds_widget_place_bet', { odds_league: league, odds_sportsbook: sportsbook, odds_selection_name: selectionName, odds_market_name: marketName, odds_event_name: eventName })
}

export function trackMarketChange(league: string, marketName: string, eventName: string) {
  track('odds_widget_market_change', { odds_league: league, odds_market_name: marketName, odds_event_name: eventName })
}

/** Expansion of the "18+ Only" responsible-gambling footer — tracked (like
 * the offer cards' terms expansion) so there's evidence the statements are
 * reachable. Fires on open only, not on collapse. */
export function trackRGExpand(league: string) {
  track('odds_widget_rg_expand', { odds_league: league })
}

// ── Approval Map widget (approval-map-sdk) ─────────────────────────────────
// Same property, own event names. map_state / map_book_kind / map_scope /
// map_tab need one-time registration as event-scoped custom dimensions in the
// GA4 UI (events collect either way — registration only affects reporting).
//
// map_state carries a USPS state code when map_scope is 'state' and an ISO
// 3166-1 alpha-2 country code when map_scope is 'country'. The scope param
// exists because the codes genuinely collide: "CA" is both California and
// Canada, so the code alone cannot be read unambiguously in a report.

type MapScope = 'state' | 'country'

/** How a tab switch was requested: the tab strip, a footer arrow, or the "view
 * state detail" jump on the US popover. Answers whether the arrows earn their
 * footprint and how often the jump gets used. */
type MapNav = 'tabs' | 'arrow' | 'jump'

export function trackMapView(league: string, theme: string, tab?: string) {
  track('map_widget_view', { odds_league: league, map_theme: theme, ...(tab ? { map_tab: tab } : {}) })
}

export function trackMapAffiliateClick(league: string, sportsbook: string, kind: string, place: string, scope: MapScope = 'state') {
  track('map_widget_affiliate_click', { odds_league: league, odds_sportsbook: sportsbook, map_book_kind: kind, map_state: place, map_scope: scope })
}

/** `status` is the colour bucket the engaged place renders as (bet / combo /
 * dfs / betpred / pred / def) — splits "browsing our coverage" from "checking
 * somewhere we're absent" in reports. */
export function trackMapStateEngage(league: string, place: string, scope: MapScope = 'state', status?: string) {
  track('map_widget_state_engage', { odds_league: league, map_state: place, map_scope: scope, ...(status ? { map_status: status } : {}) })
}

/** Tab switch on the availability map: 'usa' | 'global' | 'europe' | 'list'. */
export function trackMapTabChange(league: string, tab: string, nav: MapNav = 'tabs') {
  track('map_widget_tab_change', { odds_league: league, map_tab: tab, map_nav: nav })
}

/** List-view search, fired once the query settles (~1s idle), deduped against
 * the last tracked term. `hits` is the matched-place count — "0" is the
 * interesting value: explicit demand for somewhere we have no coverage. */
export function trackMapSearch(league: string, term: string, hits: number) {
  track('map_widget_search', { odds_league: league, map_search_term: term, map_search_hits: String(hits) })
}

/** Expansion of the map's "18+ Only" responsible-gambling footer — the map
 * counterpart of odds_widget_rg_expand. Fires on open only. */
export function trackMapRGExpand(league: string) {
  track('map_widget_rg_expand', { odds_league: league })
}

export function trackMapEmpty(league: string) {
  track('map_widget_empty', { odds_league: league })
}

export function trackMapError(league: string) {
  track('map_widget_error', { odds_league: league })
}

// ── Distribution carrier-tiles widget (distribution-sdk) ───────────────────

export function trackDistView(league: string) {
  track('dist_widget_view', { odds_league: league })
}

export function trackDistAffiliateClick(league: string, sportsbook: string) {
  track('dist_widget_affiliate_click', { odds_league: league, odds_sportsbook: sportsbook })
}

export function trackDistEmpty(league: string) {
  track('dist_widget_empty', { odds_league: league })
}

export function trackDistError(league: string) {
  track('dist_widget_error', { odds_league: league })
}
